import { useState } from "react";
import { Link } from "react-router-dom";
import { Sparkles, ArrowRight, CalendarCheck, Truck, Wrench, Warehouse, FolderKanban, AlertTriangle, CheckCircle2, Clock, Lightbulb, ChevronDown } from "lucide-react";
import { cn } from "@/lib/utils";
import { fmtDate, num } from "@/lib/format";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";

/* ------------------------------------------------------------------ helpers ---- */

export const noDate = (d) => (d ? fmtDate(d, "d. MMM yyyy") : "–");
export const noDateShort = (d) => (d ? fmtDate(d, "d. MMM") : "–");
export const weekOf = (d) => (d ? `U${fmtDate(d, "I")}` : "");

/** «Lag tilbud» is deliberately narrow: it opens a new enquiry with the need pre-filled – the quote itself follows the normal flow. */
export const quoteUrlForPlan = (plan) => {
  if (!plan) return "/henvendelser?ny=1";
  const r = plan.request || {};
  const q = new URLSearchParams({ ny: "1", persons: String(r.persons || ""), start: r.start_date || "", months: String(r.months || ""), sted: r.destination?.name || r.destination || "", plan: plan.plan_id || plan.id || "" });
  if (r.customer_id) q.set("kunde", r.customer_id);
  return `/henvendelser?${q.toString()}`;
};

export const planUrl = (plan, extra = {}) => {
  const q = new URLSearchParams({ plan: plan?.plan_id || plan?.id || "", ...extra });
  return `/planlegging/kapasitet?${q.toString()}`;
};

const ORIGIN_ICON = { depot: Warehouse, project: FolderKanban, service: Wrench, klargjoring: Wrench, retur: Truck, transport: Truck };

export const verdictTone = (s) => (!s ? "neutral" : s.sufficient && s.on_time ? "success" : s.sufficient ? "warning" : "danger");
const TONE_DOT = { success: "bg-emerald-500", warning: "bg-amber-500", danger: "bg-rose-500", neutral: "bg-border" };
const TONE_TEXT = { success: "text-emerald-700 dark:text-emerald-300", warning: "text-amber-700 dark:text-amber-300", danger: "text-rose-600 dark:text-rose-400" };

/* ------------------------------------------------------------------ brief ---- */

/** Deterministic brief from the engine – sentences, not tables. */
export const CapacityBrief = ({ sentences = [], className, meta = "Beregnet av kapasitetsmotoren på faktisk flåte, allokeringer, ledetider og aktive tilbud", testId = "capacity-brief", compact }) => {
  if (!sentences.length) return null;
  return (
    <div className={cn("relative pl-7", className)} data-testid={testId}>
      <Sparkles className="absolute left-0 top-[4px] h-4 w-4 text-primary" strokeWidth={1.75} aria-hidden />
      <p className={cn("text-foreground/90", compact ? "text-[13.5px] leading-6" : "text-[15px] leading-7 sm:text-[16px]")} data-testid={`${testId}-text`}>
        {sentences.map((s, i) => (
          <span key={i} className={cn(i === 0 && "font-medium text-foreground")}>
            {s}{" "}
          </span>
        ))}
      </p>
      {meta && <p className="mt-1 text-[11.5px] leading-4 text-muted-foreground">{meta}</p>}
    </div>
  );
};

/* ------------------------------------------------------------------ coverage summary ---- */

const Fact = ({ label, value, sub, tone, testId, mono = true }) => (
  <div className="min-w-0" data-testid={testId}>
    <div className={cn("truncate font-semibold tracking-tight tabular", mono && "font-mono", "text-[24px] leading-8 sm:text-[28px] sm:leading-9", TONE_TEXT[tone])} data-testid={`${testId}-value`}>{value}</div>
    <div className="text-[11px] uppercase tracking-wide text-muted-foreground">{label}</div>
    {sub && <div className="mt-0.5 truncate text-[11.5px] leading-4 text-muted-foreground/90">{sub}</div>}
  </div>
);

export const CoverageSummary = ({ plan, className, dense }) => {
  if (!plan?.summary) return null;
  const s = plan.summary;
  return (
    <div className={cn("grid grid-cols-2 gap-x-6 gap-y-4", dense ? "sm:grid-cols-4" : "sm:grid-cols-4 lg:gap-x-8", className)} data-testid="capacity-coverage-summary">
      <Fact label="Behov" value={num(s.need_units)} sub="senger" testId="capacity-coverage-need" />
      <Fact label="Dekket ved start" value={`${num(s.covered_at_start)}/${num(s.need_units)}`} sub={s.shortfall_at_start ? `mangler ${s.shortfall_at_start} ved oppstart` : "fra dag én"} tone={s.shortfall_at_start ? (s.sufficient ? "warning" : "danger") : "success"} testId="capacity-coverage-covered-at-start" />
      <Fact label="Full leveranse fra" value={s.full_delivery_from ? noDateShort(s.full_delivery_from) : "–"} sub={s.full_delivery_from ? `${weekOf(s.full_delivery_from)} · ${s.on_time ? "som ønsket" : "faset"}` : `${s.shortfall_total} senger mangler`} tone={s.full_delivery_from ? (s.on_time ? "success" : "warning") : "danger"} testId="capacity-coverage-full-delivery-from" />
      <Fact label="Reserve etter plan" value={num(s.reserve_after)} sub={s.reserve_later ? `+${s.reserve_later} senere i perioden` : "senger på plass ved start"} tone={s.reserve_after === 0 ? "warning" : undefined} testId="capacity-coverage-reserve" />
    </div>
  );
};

/* ------------------------------------------------------------------ sourcing plan (phase rail) ---- */

const Codes = ({ codes = [], index }) => {
  const shown = codes.slice(0, 6);
  const rest = codes.length - shown.length;
  return (
    <div className="flex flex-wrap items-center gap-1">
      {shown.map((c) => (
        <span key={c} className="kbd font-mono text-[10.5px]">{c}</span>
      ))}
      {rest > 0 && (
        <Popover>
          <PopoverTrigger asChild>
            <button type="button" className="rounded-md px-1.5 py-0.5 font-mono text-[10.5px] text-muted-foreground transition-colors duration-150 hover:bg-muted hover:text-foreground" data-testid={`capacity-sourcing-phase-${index}-modules-button`}>+{rest}</button>
          </PopoverTrigger>
          <PopoverContent align="start" className="max-h-72 w-80 overflow-y-auto p-3 shadow-pop">
            <div className="mb-2 text-[12px] font-medium">{codes.length} moduler</div>
            <div className="flex flex-wrap gap-1">{codes.map((c) => <span key={c} className="kbd font-mono text-[10.5px]">{c}</span>)}</div>
          </PopoverContent>
        </Popover>
      )}
    </div>
  );
};

export const PhaseRow = ({ ph, need, compact, showCodes = true }) => {
  const Icon = ORIGIN_ICON[ph.source_kind] || Warehouse;
  const late = !ph.on_time;
  return (
    <li className="relative grid grid-cols-[auto_1fr] gap-x-3 py-3 sm:grid-cols-[92px_1fr_auto] sm:gap-x-4" data-testid={`capacity-sourcing-phase-${ph.index}`}>
      <div className="flex flex-col items-start gap-1">
        <span className={cn("inline-flex items-center rounded-full px-2 py-1 font-mono text-[11px] tabular", late ? "bg-amber-500/10 text-amber-800 dark:text-amber-300" : "bg-muted text-foreground")} title={ph.ready_at}>
          {noDateShort(ph.ready_at)}
        </span>
        <span className="pl-1 text-[10.5px] uppercase tracking-wide text-muted-foreground">{late ? "klar (sen)" : "klar"}</span>
      </div>
      <div className="min-w-0">
        <div className="flex min-w-0 items-center gap-2">
          <Icon className="h-3.5 w-3.5 shrink-0 text-muted-foreground" strokeWidth={1.75} aria-hidden />
          <span className="truncate text-[13px] font-medium leading-5">
            {ph.origin_label}
            <span className="text-muted-foreground"> · </span>
            {ph.source_label}
          </span>
        </div>
        <div className="mt-0.5 truncate text-[11.5px] leading-4 text-muted-foreground">
          {ph.source_kind === "project" && ph.free_from ? `frigjøres ${noDateShort(ph.free_from)} → ` : ""}
          {ph.steps_label}
          {ph.type_codes && Object.keys(ph.type_codes).length > 0 && (
            <span> · {Object.entries(ph.type_codes).map(([k, v]) => `${v}×${k}`).join(", ")}</span>
          )}
        </div>
        {showCodes && !compact && ph.module_codes?.length > 0 && <div className="mt-1.5"><Codes codes={ph.module_codes} index={ph.index} /></div>}
      </div>
      <div className="col-start-2 mt-1 flex items-baseline gap-2 sm:col-start-3 sm:mt-0 sm:flex-col sm:items-end sm:gap-0">
        <span className="font-mono text-[15px] font-semibold tabular">+{ph.units}</span>
        <span className="font-mono text-[11px] tabular text-muted-foreground">{Math.min(ph.cumulative_units, need)}/{need}</span>
      </div>
    </li>
  );
};

export const SourcingPlan = ({ plan, compact, limit, className }) => {
  const [all, setAll] = useState(false);
  if (!plan?.phases) return null;
  const need = plan.request?.persons || plan.summary?.need_units || 0;
  const phases = limit && !all ? plan.phases.slice(0, limit) : plan.phases;
  return (
    <div className={className} data-testid="capacity-sourcing-plan">
      <ol className="divide-y divide-border/70">
        {phases.map((ph) => <PhaseRow key={ph.index} ph={ph} need={need} compact={compact} />)}
      </ol>
      {limit && plan.phases.length > limit && (
        <button type="button" onClick={() => setAll((v) => !v)} className="mt-1 inline-flex items-center gap-1 text-[11.5px] text-muted-foreground hover:text-foreground" data-testid="capacity-sourcing-more-button">
          {all ? "Vis færre" : `+${plan.phases.length - limit} faser til`} <ChevronDown className={cn("h-3.5 w-3.5 transition-transform duration-150", all && "rotate-180")} />
        </button>
      )}
      {!plan.phases.length && <div className="py-6 text-[12.5px] text-muted-foreground" data-testid="capacity-sourcing-empty">Ingen moduler kan gjøres klare i perioden.</div>}
    </div>
  );
};

/* ------------------------------------------------------------------ support checks ---- */

const SUPPORT_STATE = {
  ok: { Icon: CheckCircle2, dot: "bg-emerald-500", text: "dekket" },
  later: { Icon: Clock, dot: "bg-amber-500", text: "senere" },
  shortfall: { Icon: AlertTriangle, dot: "bg-rose-500", text: "mangler" },
};

export const SupportChecks = ({ support = [], className, compact }) => {
  if (!support.length) return null;
  return (
    <ul className={cn("flex flex-wrap gap-x-6 gap-y-2", className)} data-testid="capacity-support-checks">
      {support.map((s) => {
        const st = SUPPORT_STATE[s.status] || SUPPORT_STATE.ok;
        return (
          <li key={s.category} className="flex items-center gap-2 text-[12.5px]" data-testid={`capacity-support-check-${s.category}`}>
            <span className={cn("h-2 w-2 rounded-full", st.dot)} aria-hidden />
            <span className="font-medium">{s.label}</span>
            <span className="font-mono tabular text-muted-foreground">
              {s.status === "ok" ? `${s.on_time_units}/${s.need_units}` : s.status === "later" ? `${s.on_time_units}/${s.need_units} · full ${noDateShort(s.full_from)}` : `mangler ${s.shortfall_units}`}
            </span>
            {!compact && !s.required && <span className="text-[11px] text-muted-foreground">(valgfri)</span>}
          </li>
        );
      })}
    </ul>
  );
};

/* ------------------------------------------------------------------ risks + alternatives ---- */

export const RiskList = ({ risks = [], className, limit, testId = "capacity-risk-list" }) => {
  const rows = limit ? risks.slice(0, limit) : risks;
  if (!risks.length) {
    return (
      <div className={cn("flex items-center gap-2 text-[12.5px] text-muted-foreground", className)} data-testid={`${testId}-empty`}>
        <span className="h-2 w-2 rounded-full bg-emerald-500" aria-hidden /> Ingen kjente konflikter i perioden.
      </div>
    );
  }
  return (
    <ul className={cn("space-y-2.5", className)} data-testid={testId}>
      {rows.map((r, i) => (
        <li key={i} className="relative pl-3.5 text-[12.5px] leading-5" data-testid={`capacity-risk-${r.kind}`}>
          <span className={cn("absolute left-0 top-[7px] h-[calc(100%-14px)] min-h-[10px] w-[3px] rounded-full", r.severity === "danger" ? "bg-rose-500" : "bg-amber-500")} aria-hidden />
          <div className="font-medium">{r.title}</div>
          {r.detail && <div className="text-muted-foreground">{r.detail}</div>}
        </li>
      ))}
      {limit && risks.length > limit && <li className="pl-3.5 text-[11.5px] text-muted-foreground">+{risks.length - limit} til</li>}
    </ul>
  );
};

export const Alternatives = ({ items = [], onPick, className }) => {
  if (!items.length) return null;
  return (
    <ul className={cn("space-y-2", className)} data-testid="capacity-alternatives">
      {items.map((a, i) => {
        const clickable = onPick && (a.kind === "move_start" || a.kind === "move_project");
        const Comp = clickable ? "button" : "div";
        return (
          <li key={i}>
            <Comp
              type={clickable ? "button" : undefined}
              onClick={clickable ? () => onPick(a) : undefined}
              className={cn("flex w-full items-start gap-2.5 rounded-lg py-1.5 text-left text-[12.5px] leading-5", clickable && "-mx-2 px-2 transition-colors duration-150 hover:bg-muted/70 focus-visible:bg-muted/70 focus-visible:outline-none")}
              data-testid={`capacity-alternative-${a.kind}`}
            >
              <Lightbulb className="mt-1 h-3.5 w-3.5 shrink-0 text-amber-600 dark:text-amber-400" strokeWidth={1.75} aria-hidden />
              <span className="min-w-0">
                <span className="font-medium">{a.title}</span>
                {a.detail && <span className="block text-muted-foreground">{a.detail}</span>}
                {clickable && <span className="mt-0.5 inline-flex items-center gap-1 text-[11.5px] text-primary">Prøv dette <ArrowRight className="h-3 w-3" /></span>}
              </span>
            </Comp>
          </li>
        );
      })}
    </ul>
  );
};

/* ------------------------------------------------------------------ verdict ---- */

export const Verdict = ({ plan, className, compact }) => {
  if (!plan?.summary) return null;
  const tone = verdictTone(plan.summary);
  return (
    <div className={cn("flex items-start gap-2.5", className)} data-testid="capacity-verdict" data-tone={tone}>
      <span className={cn("mt-[7px] h-2.5 w-2.5 shrink-0 rounded-full", TONE_DOT[tone])} aria-hidden />
      <p className={cn("font-medium text-foreground", compact ? "text-[14px] leading-6" : "text-[17px] leading-7 sm:text-[19px] sm:leading-8")} data-testid="capacity-verdict-text">{plan.brief?.[0]}</p>
    </div>
  );
};

/* ------------------------------------------------------------------ small period line ---- */

export const RequestLine = ({ request, className }) => {
  if (!request) return null;
  const dest = request.destination?.name || request.destination?.short || request.destination || "–";
  return (
    <div className={cn("flex flex-wrap items-center gap-x-2 gap-y-1 text-[12.5px] text-muted-foreground", className)} data-testid="capacity-request-line">
      <span className="font-mono tabular text-foreground">{request.persons} personer</span>
      <span aria-hidden>·</span>
      <span className="inline-flex items-center gap-1"><CalendarCheck className="h-3.5 w-3.5" strokeWidth={1.75} aria-hidden /> {noDate(request.start_date)} – {noDate(request.end_date)}</span>
      <span aria-hidden>·</span>
      <span className="truncate">{dest}</span>
      {request.scenario && Object.keys(request.scenario).length > 0 && <span className="rounded-full bg-amber-500/10 px-2 py-0.5 text-[11px] text-amber-800 dark:text-amber-300">what-if</span>}
    </div>
  );
};

export const PlanLink = ({ plan, children = "Vis kapasitetsplan", testId = "capacity-plan-view-button" }) => (
  <Link to={planUrl(plan)} className="inline-flex items-center gap-1 text-[12.5px] font-medium text-primary hover:underline" data-testid={testId}>
    {children} <ArrowRight className="h-3.5 w-3.5" />
  </Link>
);
